// chat/actions.ts — the ChatAction set for Oriora Chores, derived ONLY from THIS app's
// store (src/store.ts). Every action maps to a real store command that already exists:
//   add_chore   → store.createChore        (add/create)
//   log_done    → store.createCompletion   (add/create — log a chore done)
//   edit_chore  → store.updateChore        (store HAS an update command)
//   delete_chore→ store.deleteChore        (HITL, confirmed in Chat.tsx before running)
//   filter      → store.listChores (+ choreDueStatus)  (local search over the member's chores)
//   summarize   → weeklyRecap via computeSignals  (the app's EXISTING live AI endpoint)
//
// Everything is scoped to the ACTIVE member (ChatCtx) — chores + completions are per-member.
// OBSERVATIONAL fence preserved: no judgement, no reward/punishment — same as the rest of app.

import {
  store, choreDueStatus, cadenceLabel, todayLocal,
  type ChorePatch,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "chores";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { weeklyRecap, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

/** The chat always acts inside one person's context (mirrors the app's active-person UI).
 *  `transcript` is the current chat text, set per-send so save_to_cloud can store the conversation. */
export interface ChatCtx { memberId: string | null; memberName: string | null; transcript?: string; }

/** A row shown after a filter/search — adapted to this app's chore fields. */
export interface FilterRow {
  id: string;
  name: string;
  points: number;
  cadence: string;
  status: string;   // "due" / "overdue Nd" / "ok" / "—"
  active: boolean;
}

/** A local, deterministic filter descriptor over the active member's chores. */
export interface ChoreQuery {
  text?: string;
  cadence?: string;        // "daily" | "weekly" | "once" | free text
  onlyDue?: boolean;       // due or overdue right now
  onlyRetired?: boolean;
  onlyActive?: boolean;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_chore"; name: string; points: number; cadence: string; rotation?: string; turnMemberId?: string }
  | { kind: "log_done"; choreId: string; label: string }
  | { kind: "edit_chore"; id: string; patch: ChorePatch; label?: string }
  | { kind: "delete_chore"; id: string; label?: string }
  | { kind: "add_member"; name: string }
  | { kind: "list_members" }
  | { kind: "edit_member"; id: string; name: string; label?: string }
  | { kind: "delete_member"; id: string; label?: string } // UI confirms; removes their chores + completions too
  | { kind: "filter"; query: ChoreQuery; label?: string }
  | { kind: "summarize" }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult { ok: boolean; message: string; changed?: boolean; rows?: FilterRow[]; }

function needMember(ctx: ChatCtx): string {
  return `Pick or add a person first — then I can help with ${ctx.memberName ?? "their"} chores.`;
}

export async function executeAction(a: ChatAction, ctx: ChatCtx): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_chore": {
        if (!ctx.memberId) return { ok: false, message: needMember(ctx) };
        // A rotating chore in ONE sentence: `rotation` is the ordered pool, `turnMemberId` is whose
        // turn it is NOW. Without these, "add a bins chore that rotates between Alex and Sam" had
        // to be two steps — create, then edit.
        const ch = await store.createChore({
          member_id: a.turnMemberId ?? ctx.memberId,
          name: a.name, points: a.points, cadence: a.cadence,
          rotation: a.rotation ?? "",
        });
        const who = a.rotation ? " — it takes turns" : "";
        return { ok: true, message: `Added “${ch.name}” (${ch.points} pt${ch.points === 1 ? "" : "s"}, ${cadenceLabel(ch.cadence)})${who}.`, changed: true };
      }

      case "log_done": {
        if (!ctx.memberId) return { ok: false, message: needMember(ctx) };
        await store.createCompletion({ member_id: ctx.memberId, chore_id: a.choreId });
        return { ok: true, message: `Logged “${a.label}” done for ${ctx.memberName ?? "them"} today.`, changed: true };
      }

      case "edit_chore":
        await store.updateChore(a.id, a.patch);
        return { ok: true, message: `Updated “${a.label ?? "the chore"}”.`, changed: true };

      case "delete_chore":
        await store.deleteChore(a.id);
        return { ok: true, message: `Deleted “${a.label ?? "the chore"}” and its logged entries.`, changed: true };

      case "add_member": {
        const m = await store.createMember({ name: a.name });
        return { ok: true, message: `Added ${m.name}. Pick them to add and log their chores.`, changed: true };
      }

      case "list_members": {
        const ms = await store.listMembers();
        if (!ms.length) return { ok: true, message: "No one added yet — say e.g. “add person Alex”." };
        return { ok: true, message: `People: ${ms.map((m) => m.name).join(", ")}.` };
      }

      case "edit_member":
        await store.updateMember(a.id, { name: a.name });
        return { ok: true, message: `Renamed ${a.label ?? "them"} to ${a.name}.`, changed: true };

      case "delete_member":
        await store.deleteMember(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "that person"} and all their chores and logged entries.`, changed: true };

      case "filter": {
        if (!ctx.memberId) return { ok: false, message: needMember(ctx) };
        const [chores, comps] = await Promise.all([
          store.listChores(ctx.memberId),
          store.listCompletions(ctx.memberId),
        ]);
        const lastByChore = new Map<string, string>();
        for (const c of comps) if (!lastByChore.has(c.chore_id)) lastByChore.set(c.chore_id, c.done_on); // list is newest-first
        const q = a.query;
        const term = (q.text ?? "").trim().toLowerCase();
        const cad = (q.cadence ?? "").trim().toLowerCase();
        const matched = chores.filter((ch) => {
          if (term && !ch.name.toLowerCase().includes(term)) return false;
          if (cad && ch.cadence.trim().toLowerCase() !== cad) return false;
          if (q.onlyActive && ch.active === 0) return false;
          if (q.onlyRetired && ch.active !== 0) return false;
          if (q.onlyDue) {
            const st = choreDueStatus(ch.cadence, lastByChore.get(ch.id) ?? null, undefined, ch.due_on);
            if (!st || st.status === "ok") return false;
          }
          return true;
        });
        const rows: FilterRow[] = matched.slice(0, 50).map((ch) => {
          const st = choreDueStatus(ch.cadence, lastByChore.get(ch.id) ?? null, undefined, ch.due_on);
          const status = st ? (st.status === "overdue" ? `overdue ${st.days}d` : st.status) : "—";
          return { id: ch.id, name: ch.name, points: ch.points, cadence: ch.cadence, status, active: ch.active !== 0 };
        });
        const noun = matched.length === 1 ? "chore" : "chores";
        return { ok: true, message: matched.length ? `${matched.length} ${noun}:` : "No chores match that.", rows };
      }

      case "summarize": {
        if (!ctx.memberId) return { ok: false, message: needMember(ctx) };
        const signals = await store.computeSignals(ctx.memberId, todayLocal(), 7);
        const { recap } = await weeklyRecap(signals);
        return { ok: true, message: recap || "Nothing to recap yet." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my chores" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        const content = a.contentKind === "conversation" ? (ctx.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation"
            ? "There's nothing to save yet — chat with me a bit first, then ask again."
            : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
